import React from 'react';
import { CASH_ALERT_CENTS, DAYS_ALERT, ONLINE_WINDOW_MINUTES } from './pinCard';
import {
  DEFAULT_FILTERS,
  formatProvinceLabel,
  hasActiveFilters,
  listProvinces,
  toggleProvince,
} from './mapFilters';
import { DEFAULT_MARKER_STYLE, MARKER_STYLE_OPTIONS } from './markerStyle';
import './MapFilterBar.css';

// DJB-242: search bar + filter toggles above the Accounts › Maps map.
// Controlled component: `filters` is the mapFilters.js state shape,
// `onChange` receives the next state. Province chips come from the machines
// on the map (default: all switched on); `provinceCounts` are the per-chip
// badges (countPerProvince — machines the province would add given the other
// filters). Everything combines (AND) in mapFilters.applyFilters.
//
// DJB-243: the Standard / Detail segmented toggle next to the search bar
// switches the pin style (markerStyle.js); `markerStyle` / `onMarkerStyleChange`
// are controlled the same way as the filters.
// DJB-245: the "Online" chip joins the cash / days toggles.
// DJB-257: the "Open" chip (open today) sits right after Online.
// DJB-246: province chips are compact and show normal capitalisation
// (formatProvinceLabel) — the filter value itself stays the raw province.

export const SEARCH_PLACEHOLDER = 'Zoek op naam of plaats…';
export const CASH_TOGGLE_LABEL = `cash > €${CASH_ALERT_CENTS / 100}`;
export const DAYS_TOGGLE_LABEL = `days > ${DAYS_ALERT}`;
export const ONLINE_TOGGLE_LABEL = 'Online';
export const OPEN_TOGGLE_LABEL = 'Open';
export const MARKER_STYLE_GROUP_LABEL = 'Markerstijl';

const MapFilterBar = ({
  machines,
  filters = DEFAULT_FILTERS,
  provinceCounts = {},
  onChange,
  markerStyle = DEFAULT_MARKER_STYLE,
  onMarkerStyleChange,
}) => {
  const provinces = listProvinces(machines);
  const hidden = filters.hiddenProvinces || [];
  const update = (patch) => onChange({ ...filters, ...patch });

  return (
    <div className="map-filters" data-testid="map-filters">
      <div className="map-filters-top">
        <div className="map-filters-search">
          <svg
            className="map-filters-search-icon"
            viewBox="0 0 16 16"
            width="14"
            height="14"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="6.75" cy="6.75" r="4.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10.4 10.4 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            className="map-filters-search-input"
            placeholder={SEARCH_PLACEHOLDER}
            aria-label="Zoek machines op naam of plaats"
            value={filters.query}
            onChange={(event) => update({ query: event.target.value })}
          />
          {filters.query && (
            <button
              type="button"
              className="map-filters-search-clear"
              aria-label="Zoekterm wissen"
              onClick={() => update({ query: '' })}
            >
              &times;
            </button>
          )}
        </div>

        <div
          className="map-marker-style"
          role="radiogroup"
          aria-label={MARKER_STYLE_GROUP_LABEL}
          data-testid="marker-style-toggle"
        >
          {MARKER_STYLE_OPTIONS.map((option) => {
            const active = markerStyle === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`map-marker-style-option is-${option.value}${active ? ' is-active' : ''}`}
                title={
                  option.value === 'detail'
                    ? 'Markers kleuren op cash > €150 (geel) en days > 90 (oranje)'
                    : 'Alle markers in dezelfde kleur'
                }
                onClick={() => {
                  if (!active && onMarkerStyleChange) onMarkerStyleChange(option.value);
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="map-filters-row">
        <div
          className="map-filters-group map-filters-provinces"
          role="group"
          aria-label="Provincies"
        >
          {provinces.length === 0 && (
            <span className="map-filters-empty">Geen provincies</span>
          )}
          {provinces.map((province) => {
            const visible = !hidden.includes(province);
            const count = provinceCounts[province] || 0;
            return (
              <button
                key={province}
                type="button"
                className={`map-filter-chip map-filter-province${visible ? ' is-on' : ''}`}
                aria-pressed={visible}
                title={
                  visible
                    ? `${formatProvinceLabel(province)} verbergen`
                    : `${formatProvinceLabel(province)} tonen`
                }
                onClick={() => onChange(toggleProvince(filters, province))}
              >
                <span className="map-filter-chip-label">{formatProvinceLabel(province)}</span>
                <span className="map-filter-chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="map-filters-group map-filters-alerts" role="group" aria-label="Alleen tonen">
          <button
            type="button"
            className={`map-filter-chip map-filter-alert is-cash${filters.cashAlertOnly ? ' is-on' : ''}`}
            aria-pressed={filters.cashAlertOnly}
            title="Alleen machines met meer dan €150 cash"
            onClick={() => update({ cashAlertOnly: !filters.cashAlertOnly })}
          >
            <span className="map-filter-chip-label">{CASH_TOGGLE_LABEL}</span>
          </button>
          <button
            type="button"
            className={`map-filter-chip map-filter-alert is-days${filters.daysAlertOnly ? ' is-on' : ''}`}
            aria-pressed={filters.daysAlertOnly}
            title="Alleen machines met meer dan 90 dagen sinds de laatste cashout"
            onClick={() => update({ daysAlertOnly: !filters.daysAlertOnly })}
          >
            <span className="map-filter-chip-label">{DAYS_TOGGLE_LABEL}</span>
          </button>
          <button
            type="button"
            className={`map-filter-chip map-filter-alert is-online${filters.onlineOnly ? ' is-on' : ''}`}
            aria-pressed={filters.onlineOnly}
            title={`Alleen machines die online zijn (gezien in de laatste ${ONLINE_WINDOW_MINUTES} min)`}
            onClick={() => update({ onlineOnly: !filters.onlineOnly })}
          >
            <span className="map-filter-chip-dot" aria-hidden="true" />
            <span className="map-filter-chip-label">{ONLINE_TOGGLE_LABEL}</span>
          </button>
          <button
            type="button"
            className={`map-filter-chip map-filter-alert is-open${filters.openOnly ? ' is-on' : ''}`}
            aria-pressed={Boolean(filters.openOnly)}
            title="Alleen locaties die vandaag open zijn"
            onClick={() => update({ openOnly: !filters.openOnly })}
          >
            <span className="map-filter-chip-dot" aria-hidden="true" />
            <span className="map-filter-chip-label">{OPEN_TOGGLE_LABEL}</span>
          </button>
          {hasActiveFilters(filters) && (
            <button
              type="button"
              className="map-filters-reset"
              onClick={() => onChange({ ...DEFAULT_FILTERS })}
            >
              Alles tonen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapFilterBar;
